import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Country, State } from 'country-state-city';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { useAccountToast } from '../features/wallet/hooks/useWalletTransferNotifications';
import {
  resolveClientCountryAndState,
  type ClientCountryOption,
  type ClientStateOption,
} from '../utils/clientProfileDisplay';

export function useAccountSettingsState() {
  const navigate = useNavigate();
  const { client, refreshClient, logout } = useAuth();
  const { toast, showSuccess, showError, closeToast } = useAccountToast();

  const [name, setName] = useState(client?.name || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const [phone, setPhone] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const [country, setCountry] = useState<ClientCountryOption | null>(null);
  const [state, setState] = useState<ClientStateOption | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => (country ? State.getStatesOfCountry(country.isoCode) : []),
    [country]
  );

  const [dpLoading, setDpLoading] = useState(false);
  const [dpError, setDpError] = useState('');
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    void refreshClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (client?.name) setName(client.name);
  }, [client?.name]);

  useEffect(() => {
    if (!client) return;
    setPhone(client.phone || '');
    const resolved = resolveClientCountryAndState(client);
    setCountry(resolved.country);
    setState(resolved.state);
  }, [client]);

  const handleSaveName = useCallback(async (): Promise<boolean> => {
    if (!name.trim()) {
      setNameError('Name is required');
      return false;
    }

    setNameLoading(true);
    setNameError('');

    try {
      await authService.updateProfile({ name: name.trim() });
      await refreshClient();
      showSuccess('Profile updated', 'Your display name has been saved.');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update name';
      setNameError(message);
      showError('Could not update name', message);
      return false;
    } finally {
      setNameLoading(false);
    }
  }, [name, refreshClient, showError, showSuccess]);

  const handleSavePhone = useCallback(async (): Promise<boolean> => {
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 7) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }

    setPhoneLoading(true);
    setPhoneError('');

    try {
      await authService.updateProfile({ phone });
      await refreshClient();
      showSuccess('Phone updated', 'Your phone number has been saved.');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update phone';
      setPhoneError(message);
      showError('Could not update phone', message);
      return false;
    } finally {
      setPhoneLoading(false);
    }
  }, [phone, refreshClient, showError, showSuccess]);

  const handleSaveLocation = useCallback(async (): Promise<boolean> => {
    if (!country || !state) {
      setLocationError('Please select country and province/state');
      return false;
    }

    setLocationLoading(true);
    setLocationError('');

    try {
      await authService.updateProfile({
        address: { country: country.isoCode, state_province: state.isoCode },
      });
      await refreshClient();
      showSuccess('Location updated', 'Your location has been saved.');
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update location';
      setLocationError(message);
      showError('Could not update location', message);
      return false;
    } finally {
      setLocationLoading(false);
    }
  }, [country, state, refreshClient, showError, showSuccess]);

  const handleChangePassword = useCallback(async () => {
    setPasswordError('');

    if (client?.has_password && !currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (!newPassword) {
      setPasswordError('New password is required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);

    try {
      await authService.changePassword(
        client?.has_password ? currentPassword : null,
        newPassword
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      await refreshClient();
      showSuccess('Password updated', 'Your sign-in password has been changed.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      setPasswordError(message);
      showError('Could not update password', message);
    } finally {
      setPasswordLoading(false);
    }
  }, [
    client?.has_password,
    confirmPassword,
    currentPassword,
    newPassword,
    refreshClient,
    showError,
    showSuccess,
  ]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        setDpError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setDpError('Image must be less than 5MB');
        return;
      }

      setDpLoading(true);
      setDpError('');

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result as string;
          await authService.updateProfilePicture(base64);
          await refreshClient();
          showSuccess('Photo updated', 'Your profile picture has been saved.');
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to update profile picture';
          setDpError(message);
          showError('Could not update photo', message);
        } finally {
          setDpLoading(false);
        }
      };
      reader.onerror = () => {
        setDpError('Failed to read image file');
        setDpLoading(false);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    },
    [refreshClient, showError, showSuccess]
  );

  const handleLogout = useCallback(async () => {
    setLogoutLoading(true);
    try {
      await logout();
      navigate('/login');
    } finally {
      setLogoutLoading(false);
    }
  }, [logout, navigate]);

  return {
    client,
    name,
    nameLoading,
    nameError,
    currentPassword,
    newPassword,
    confirmPassword,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    passwordLoading,
    passwordError,
    phone,
    phoneLoading,
    phoneError,
    country,
    state,
    countries,
    states,
    locationLoading,
    locationError,
    dpLoading,
    dpError,
    logoutLoading,
    toast,
    closeToast,
    setName,
    setPhone,
    setPhoneError,
    setCountry,
    setState,
    setLocationError,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    setShowCurrentPassword,
    setShowNewPassword,
    setShowConfirmPassword,
    setPasswordError,
    setDpError,
    handleSaveName,
    handleSavePhone,
    handleSaveLocation,
    handleChangePassword,
    handleFileChange,
    handleLogout,
  };
}
